import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

const AUTH_BG = "https://images.pexels.com/photos/31732696/pexels-photo-31732696.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({ name: "", password: "", company: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        if (cancelled) return;
        setInvite(data);
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(formatApiErrorDetail(err.response?.data?.detail) || "Invite not found.");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, form);
      if (setUser) setUser(data);
      toast.success("Welcome to Inverse.");
      if (data.auction_id) navigate(`/auctions/${data.auction_id}`);
      else navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="accept-invite-loading">
        <div className="text-xs tracking-[0.3em] uppercase text-neutral-500">Loading invite...</div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="accept-invite-error">
        <div className="max-w-md text-center">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Invite link</div>
          <h2 className="font-display font-extrabold text-3xl tracking-tightest mb-3">Invite unavailable</h2>
          <p className="text-sm text-neutral-600 mb-6">{errorMsg}</p>
          <Link to="/login">
            <Button className="rounded-none bg-[#0B1929] text-white h-11 px-6 btn-sharp" data-testid="accept-invite-goto-login">
              Go to login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="accept-invite-page">
      <div className="hidden lg:block relative bg-[#0B1929] overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(1200px 600px at 20% 30%, rgba(34,211,238,0.18), transparent 60%)," +
              "radial-gradient(900px 500px at 80% 70%, rgba(13,148,136,0.22), transparent 60%)," +
              "#0B1929",
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-[#22D3EE] flex items-center justify-center">
              <Gavel size={22} weight="bold" color="#0B1929" />
            </div>
            <div style={{ fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }}>
              INVERSE<span className="text-[#22D3EE]">.</span>
            </div>
          </div>
          <div className="text-white max-w-xl">
            <div className="text-xs tracking-[0.35em] uppercase mb-6 text-[#22D3EE] font-semibold">Invited Bidder</div>
            <h1 style={{ fontSize: "4rem", lineHeight: 1.05, letterSpacing: "-0.03em", fontWeight: 800 }}>
              Set a password.<br />
              <span className="text-white/50">Quote in private.</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-md">
              Your bids stay hidden from other invitees. The auction creator only sees the lowest price — never your name or company — until the auction is closed.
            </p>
            <div className="mt-10 pt-6 border-t border-white/10 text-xs text-white/60 tracking-[0.25em] uppercase">
              A Radish Technologies Platform
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0D9488] font-semibold mb-3">Accept Invitation</div>
          <h2 className="font-display text-3xl mb-2 text-[#0B1929]">You&apos;re invited</h2>
          {invite?.role === "admin" && (
            <div className="inline-block bg-[#22D3EE] text-[#0B1929] px-2 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold mb-4" data-testid="invite-role-badge">
              Admin role
            </div>
          )}
          {invite?.auction_title ? (
            <>
              <p className="text-sm text-neutral-700 mb-1">
                <strong>{invite.invited_by || "An admin"}</strong> invited you to bid on:
              </p>
              <p className="font-display font-extrabold text-xl tracking-tightest mb-6">{invite.auction_title}</p>
            </>
          ) : (
            <p className="text-sm text-neutral-700 mb-6">
              <strong>{invite?.invited_by || "An admin"}</strong> invited you to join Inverse as a{" "}
              <strong>{invite?.role === "admin" ? "platform admin" : "bidder"}</strong>.
            </p>
          )}
          <div className="border border-black/10 p-3 mb-6 bg-neutral-50">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Email</div>
            <div className="text-sm font-mono-plex" data-testid="invite-email">{invite?.email}</div>
          </div>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Your Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none border-black/20 h-11" data-testid="invite-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Company (optional)</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none border-black/20 h-11" data-testid="invite-company-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Set Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="rounded-none border-black/20 h-11" data-testid="invite-password-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-11 btn-sharp" data-testid="invite-submit">
              {submitting ? "Creating account..." : "Accept & continue"}
              <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </form>

          <div className="mt-6 text-xs text-neutral-500">
            Already have an account?{" "}
            <Link to="/login" className="text-black font-semibold underline underline-offset-4" data-testid="goto-login-from-invite">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
